import Constants from 'Components/ContractGenerator/shared/constants';
import eachDeep from 'deepdash/eachDeep';
import { cloneDeep, isArray, mergeWith, set, unset } from 'lodash';

import types from './types';

const initialState = {
	windowSize: {
		width: null,
		height: null
	},
	contractId: '',
	sessionId: '',
	user: {
		isLoggedIn: false,
		sessionId: ''
	},
	extensions: {},
	products: {},
	forms: [],
	formFields: {},
	constants: {},
	activeStep: Constants.STEPS.STEP1,
	userInput: {
		blockedFields: {},
		dataToSubmit: {
			selectedAccommodationType: 'HOTEL',
			selectedRooms: 20,
			selectedProduct: {
				id: null,
				options: {}
			},
			selectedExtensions: {
				ids: [],
				options: {},
				selectedExtensionOptions: {}
			},
			selectedContractTermInMonths: 24,
			selectedPaymentType: 'yearly',
			formData: {},
			captchaResponse: ''
		},
		fieldInfo: {}
	},
	discountSettings: {
		dataToSubmit: {},
		fieldInfo: {}
	}
};

function contractGeneratorReducer(state = initialState, action) {
	switch (action.type) {
		case types.POST_CALLS.SUBMIT.SUCCESS: {
			return {
				...state,
				contractId: action.payload.success.contractId
			};
		}

		case types.POST_CALLS.LOGIN.SUCCESS: {
			return {
				...state,
				user: {
					isLoggedIn: true,
					sessionId: action.payload.success.sessionId
				}
			};
		}

		case types.GET_CALLS.CHECK_USER_SESSION.SUCCESS:
			return {
				...state,
				user: {
					...state.user,
					sessionId: action.payload.success.sessionId,
					isLoggedIn: action.payload.success.isLoggedIn
				}
			};

		case types.POST_CALLS.SAVE_SESSION.SUCCESS: {
			return {
				...state,
				sessionId: action.payload.success.sessionId
			};
		}

		case types.GET_CALLS.FETCH_SESSION.SUCCESS:
			return {
				...state,
				userInput: {
					...state.userInput,
					dataToSubmit: { ...state.userInput.dataToSubmit, ...action.payload.dataToSubmit },
					blockedFields: action.payload.blockedFields
				},
				activeStep: Constants.STEPS.STEP2
			};

		case types.GET_CALLS.FETCH_EXTENSIONS.SUCCESS:
			return {
				...state,
				extensions: { ...action.payload }
			};

		case types.GET_CALLS.FETCH_PRODUCTS.SUCCESS:
			return {
				...state,
				products: { ...action.payload }
			};

		case types.GET_CALLS.FETCH_ENABLE_STEP_2_FORM.SUCCESS:
			return {
				...state,
				enableStep2Form: [...action.payload]
			};

		case types.GET_CALLS.FETCH_FORMS.SUCCESS:
			return {
				...state,
				forms: [...action.payload]
			};

		case types.GET_CALLS.FETCH_FORM_FIELDS.SUCCESS:
			return {
				...state,
				formFields: { ...action.payload }
			};

		case types.GET_CALLS.FETCH_CONSTANTS.SUCCESS:
			return {
				...state,
				constants: { ...action.payload }
			};

		case types.SET_WINDOW_SIZE:
			return {
				...state,
				windowSize: action.payload
			};

		case types.SET_ACTIVE_STEP:
			return {
				...state,
				activeStep: action.payload
			};

		case types.SET_DATA_TO_SUBMIT: {
			const dataToMerge = cloneDeep({ ...state.userInput.dataToSubmit });
			const newDataToMerge = cloneDeep({ ...action.payload });
			const newDataToSubmit = mergeWith(dataToMerge, newDataToMerge, (a, b) => (isArray(b) ? b : undefined));
			return {
				...state,
				userInput: {
					...state.userInput,
					dataToSubmit: newDataToSubmit
				}
			};
		}

		case types.SET_FIELD_INFO: {
			const dataToMerge = cloneDeep({ ...state.userInput.fieldInfo });
			const newDataToMerge = cloneDeep({ ...action.payload });
			const newFieldInfo = mergeWith(dataToMerge, newDataToMerge, (a, b) => (isArray(b) ? b : undefined));
			return {
				...state,
				userInput: {
					...state.userInput,
					fieldInfo: newFieldInfo
				}
			};
		}

		case types.SET_DISCOUNT_DATA_TO_SUBMIT: {
			const dataToMerge = cloneDeep({ ...state.discountSettings.dataToSubmit });
			const newDataToMerge = cloneDeep({ ...action.payload });
			const newDataToSubmit = mergeWith(dataToMerge, newDataToMerge, (a, b) => (isArray(b) ? b : undefined));
			return {
				...state,
				discountSettings: {
					...state.discountSettings,
					dataToSubmit: newDataToSubmit
				}
			};
		}

		case types.SET_DISCOUNT_FIELD_INFO: {
			const dataToMerge = cloneDeep({ ...state.discountSettings.fieldInfo });
			const newDataToMerge = cloneDeep({ ...action.payload });
			const newFieldInfo = mergeWith(dataToMerge, newDataToMerge, (a, b) => (isArray(b) ? b : undefined));
			return {
				...state,
				discountSettings: {
					...state.discountSettings,
					fieldInfo: newFieldInfo
				}
			};
		}

		case types.SET_ERROR_MESSAGES_VISIBLE: {
			const status = action.payload;
			const newFieldInfo = cloneDeep({ ...state.userInput.fieldInfo });

			eachDeep(newFieldInfo, (a, b, c, d) => {
				if (typeof a?.showErrorMessage !== 'undefined') {
					// eslint-disable-next-line no-underscore-dangle
					set(newFieldInfo, `${d._item.strPath}.showErrorMessage`, status);
				}
			});

			return {
				...state,
				userInput: {
					...state.userInput,
					fieldInfo: newFieldInfo
				}
			};
		}

		case types.RESET_STORE: {
			return {
				...initialState,
				products: state.products,
				extensions: state.extensions,
				forms: state.forms,
				formFields: state.formFields,
				constants: state.constants,
				user: state.user
			};
		}

		case types.DELETE_KEY: {
			const stateClone = cloneDeep({ ...state });
			unset(stateClone, action.payload);

			return { ...stateClone };
		}

		default:
			return state;
	}
}

export default contractGeneratorReducer;
